<script lang="ts">
	import type { SbColor, SbImage, SbLink } from '../types';
	import { getColorTheme } from '../color';
	import PageGrid16 from '../components/pageGrid16.svelte';
	import SbRichtext from '../components/sbRichtext.svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import { getSbUrl } from '$lib/utils/storyblok/url';
	import Image from '../components/image.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';

	interface Props {
		content: ISbStoryData<{
			title: ISbRichtext;
			text: ISbRichtext;
			mobile_images: SbImage[];
			desktop_images: SbImage[];
			bg_color: SbColor;
			text_color: SbColor;
			image_position: 'left' | 'right';
			primary_cta_enabled: boolean;
			primary_cta_label: string;
			primary_cta_link: SbLink;
			secondary_cta_enabled: boolean;
			secondary_cta_label: string;
			secondary_cta_link: SbLink;
			has_transparent_mobile_navigation?: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let bgTheme = $derived(getColorTheme(content.bg_color));
	let btnTheme = $derived(bgTheme === 'dark' ? 'btn-white' : 'btn-brown');
</script>

<section
	id="hero"
	class="hero-bg-{bgTheme} bg-{content.bg_color}"
	use:editable={content}
	style="--mobile-hero-padding: {content.has_transparent_mobile_navigation
		? '0'
		: 'var(--full-header-height)'}"
>
	<PageGrid16 class="relative h-full" gridClass="h-full">
		<div class="col-span-full flex overflow-hidden lg:hidden lg:min-h-[50svh]">
			{#each content.mobile_images as image}
				<Image
					wrapperClass="w-full h-full flex-1"
					class="h-full w-full object-cover"
					src={image.filename}
					alt={image.alt}
					title={image.title}
					width={860}
				/>
			{/each}
		</div>
		<div
			class={[
				'absolute bottom-5 top-0 flex w-1/2 flex-col overflow-hidden rounded-2xl max-lg:hidden',
				{
					'lg:right-0': content.image_position === 'right',
					'lg:left-0': content.image_position !== 'right'
				}
			]}
		>
			{#each content.desktop_images as image}
				<Image
					wrapperClass="w-full h-full flex-1"
					class="h-full w-full object-cover"
					src={image.filename}
					alt={image.alt}
					title={image.title}
					width={1400}
				/>
			{/each}
		</div>
		<div
			class={[
				'col-span-full flex items-end max-lg:p-5',
				{
					'lg:col-start-1 lg:col-end-8': content.image_position === 'right',
					'lg:col-start-10 lg:col-end-[17]': content.image_position !== 'right'
				}
			]}
		>
			<div
				class="flex h-full flex-col justify-center gap-y-4 lg:justify-end lg:gap-y-5 text-{content.text_color} lg:pb-[68px]"
			>
				<div
					class="text-[52px]/[108%] font-semibold tracking-[-1.04px] lg:text-[80px]/[100%] lg:tracking-[-1.6px]"
				>
					<SbRichtext text={content.title} />
				</div>
				<div
					class="text-[18px]/[150%] tracking-[-.18px] lg:text-[24px]/[140%] lg:tracking-[-.24px]"
				>
					<SbRichtext text={content.text} />
				</div>
				{#if content.primary_cta_enabled}
					<div class="mt-3 flex gap-x-3">
						<Button
							class={btnTheme}
							href={getSbUrl(content.primary_cta_link)}
							target={content.primary_cta_link.target}>{content.primary_cta_label}</Button
						>
						{#if content.secondary_cta_enabled}
							<Button
								href={getSbUrl(content.secondary_cta_link)}
								target={content.secondary_cta_link.target}
								class="btn-secondary {btnTheme}">{content.secondary_cta_label}</Button
							>
						{/if}
					</div>
				{/if}
			</div>
		</div>
	</PageGrid16>
</section>

<style lang="scss">
	section {
		height: calc(100svh - var(--banner-height));
		padding-top: var(--mobile-hero-padding);
		transition-property: padding-top;
		transition-duration: 0.2s;

		@media (min-width: 1024px) {
			padding-top: var(--full-header-height);
		}

		@media (max-width: 640px) and (orientation: portrait) {
			:global(div.grid-wrapper > div.grid-16) {
				grid-template-rows: 1fr 1fr;
			}
		}
	}
</style>
